import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = 'be_location';
const LocationContext = createContext(null);

export const POPULAR_CITIES = ['Mumbai', 'Bangalore', 'Gurgaon', 'Pune', 'Delhi', 'Noida', 'Hyderabad', 'Ahmedabad'];

export const LocationProvider = ({ children }) => {
  const [location, setLocationState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || null; } catch { return null; }
  });
  const [status, setStatus] = useState(location ? 'ready' : 'idle'); // idle | prompting | detecting | ready | denied
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`${KEY}_recent`)) || []; } catch { return []; }
  });

  const setLocation = useCallback((loc) => {
    if (!loc?.city) return;
    const next = { city: loc.city, locality: loc.locality || null, lat: loc.lat ?? null, lng: loc.lng ?? null, source: loc.source || 'manual' };
    setLocationState(next);
    localStorage.setItem(KEY, JSON.stringify(next));
    setRecent((r) => {
      const list = [next.city, ...r.filter((c) => c !== next.city)].slice(0, 5);
      localStorage.setItem(`${KEY}_recent`, JSON.stringify(list));
      return list;
    });
    setStatus('ready');
  }, []);

  const detect = useCallback(() => {
    if (!navigator.geolocation) { setStatus('denied'); return; }
    setStatus('detecting');
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const r = await fetch(`${API}/geo/detect?lat=${coords.latitude}&lng=${coords.longitude}`);
          const d = await r.json();
          if (d.city) setLocation({ ...d, source: 'gps' });
          else setStatus('denied');
        } catch { setStatus('denied'); }
      },
      () => setStatus('denied'),
      { timeout: 8000, maximumAge: 600000 }
    );
  }, [setLocation]);

  useEffect(() => {
    if (!location && status === 'idle') setStatus('prompting');
  }, [location, status]);

  return (
    <LocationContext.Provider value={{ location, status, detect, setLocation, recent, dismissPrompt: () => setStatus('denied') }}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocationCtx = () => useContext(LocationContext);
